/**
 * Program roadmap brochures, keyed by the same page names the forms already
 * pass around ("CPA" | "CMA" | "EA").
 */
export const roadmapPdfUrls: Record<string, string> = {
  CPA: "https://ik.imagekit.io/mileseducation/miles_website/pdf/CPA_Roadmap.pdf",
  CMA: "https://ik.imagekit.io/mileseducation/miles_website/pdf/CMA_Roadmap.pdf",
  EA: "https://ik.imagekit.io/mileseducation/miles_website/pdf/EA_Roadmap.pdf",
};

/** Clicks a hidden anchor. Split out so both paths below share it. */
const clickDownload = (href: string, fileName: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Saves the roadmap to the visitor's device. It never opens the PDF in a tab —
 * both paths here end in a download.
 *
 * Preferred path is fetch-to-blob straight off the CDN: it costs us no
 * bandwidth, and the blob URL is same-origin, which is what makes `download`
 * apply at all (the attribute is ignored on cross-origin hrefs, and these
 * PDFs live on ImageKit).
 *
 * Fallback is our own /api/brochure, which streams the file back under
 * `Content-Disposition: attachment`. That covers the two cases the blob can't:
 * a failed cross-origin fetch, and iOS Safari, which frequently ignores
 * `download` on a blob URL and renders the PDF instead. With the header set
 * server-side the browser has no such choice to make.
 */
export const downloadRoadmapPdf = async (pageName: string) => {
  const pdfUrl = roadmapPdfUrls[pageName];
  if (!pdfUrl) return;

  const fileName = `${pageName.replace(/_/g, "")}_Roadmap.pdf`;

  // iOS Safari is sent straight to the server route — the blob attempt there
  // tends to "succeed" while showing the PDF, which is the one outcome this
  // function is meant to avoid, and there's no way to detect it after the fact.
  const isIos =
    /iP(hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  if (isIos) {
    clickDownload(
      `/api/brochure?course=${encodeURIComponent(pageName)}`,
      fileName,
    );
    return;
  }

  try {
    const response = await fetch(pdfUrl);
    if (!response.ok) throw new Error(`Upstream ${response.status}`);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    clickDownload(blobUrl, fileName);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error("Error downloading roadmap PDF:", error);
    clickDownload(
      `/api/brochure?course=${encodeURIComponent(pageName)}`,
      fileName,
    );
  }
};
